import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { 
  ArrowLeft, MapPin, Clock, Truck, Phone, Mail, Navigation,
  Loader2, Camera, CheckCircle, Play, Package, FileText,
  AlertTriangle, ChevronRight, X
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import SOSButton from '../../components/SOSButton';

const DriverLoadDetail = () => {
  const navigate = useNavigate();
  const { loadId } = useParams();
  const [load, setLoad] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showPODDialog, setShowPODDialog] = useState(false);
  const [statusNotes, setStatusNotes] = useState('');
  const [podForm, setPodForm] = useState({ recipient_name: '', delivery_notes: '' });
  const signatureCanvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('driver_token');
    if (!token) {
      navigate('/driver/login');
      return;
    }
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    fetchLoad();
  }, [loadId, navigate]);

  const fetchLoad = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/driver/loads/${loadId}`);
      setLoad(res.data);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/driver/login');
      } else {
        toast.error('Failed to load details');
        navigate('/driver/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const getCurrentLocation = () => {
    return new Promise((resolve) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          }),
          () => resolve(null),
          { timeout: 10000 }
        );
      } else {
        resolve(null);
      }
    });
  };

  const handleUpdateStatus = async (newStatus) => {
    setUpdating(true);
    try {
      const location = await getCurrentLocation();
      
      await api.post(`/driver/loads/${loadId}/status`, {
        status: newStatus,
        notes: statusNotes,
        latitude: location?.latitude,
        longitude: location?.longitude
      });
      
      toast.success(`Status updated to ${newStatus.replace('_', ' ')}`);
      setShowStatusDialog(false);
      setStatusNotes('');
      fetchLoad();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleSubmitPOD = async () => {
    if (!podForm.recipient_name) {
      toast.error('Please enter recipient name');
      return;
    }
    
    // Get signature data from canvas
    const canvas = signatureCanvasRef.current;
    const signatureData = canvas?.toDataURL() || '';
    
    if (!signatureData || signatureData === 'data:,') {
      toast.error('Please capture signature');
      return;
    }
    
    setUpdating(true);
    try {
      const location = await getCurrentLocation();
      
      await api.post(`/driver/loads/${loadId}/pod`, null, {
        params: {
          recipient_name: podForm.recipient_name,
          signature_data: signatureData,
          delivery_notes: podForm.delivery_notes,
          latitude: location?.latitude,
          longitude: location?.longitude
        }
      });
      
      toast.success('Delivery completed!');
      setShowPODDialog(false);
      fetchLoad();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit POD');
    } finally {
      setUpdating(false);
    }
  };

  const openNavigation = (location) => {
    const encoded = encodeURIComponent(location);
    // Try to open in maps app
    window.open(`https://www.google.com/maps/search/?api=1&query=${encoded}`, '_blank');
  };

  const callNumber = (phone) => {
    window.location.href = `tel:${phone}`;
  };

  // Signature canvas handlers
  const startDrawing = (e) => {
    const canvas = signatureCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = signatureCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#4ADE80';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const getNextStatus = () => {
    switch (load?.assignment_status) {
      case 'assigned': return 'picked_up';
      case 'picked_up': return 'in_transit';
      case 'in_transit': return 'delivered';
      default: return null;
    }
  };

  const getStatusButtonText = () => {
    switch (load?.assignment_status) {
      case 'assigned': return 'Mark as Picked Up';
      case 'picked_up': return 'Start Transit';
      case 'in_transit': return 'Complete Delivery';
      default: return 'Update Status';
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#071423] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#4ADE80]" />
      </div>
    );
  }

  if (!load) {
    return (
      <div className="min-h-screen bg-[#071423] flex items-center justify-center p-4">
        <Card className="border-[#3B82F6]/20 bg-[#0D1B2A]">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="w-12 h-12 mx-auto text-yellow-400 mb-3" />
            <p className="text-white">Load not found</p>
            <Button onClick={() => navigate('/driver/dashboard')} className="mt-4">
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isDelivered = load.assignment_status === 'delivered';

  return (
    <div className="min-h-screen bg-[#071423] pb-24">
      {/* Header */}
      <div className="bg-[#0D1B2A] border-b border-[#3B82F6]/20 px-4 py-3 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/driver/dashboard')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-bold text-white">Load Details</h1>
            <p className="text-xs text-gray-400">{load.vehicle_info}</p>
          </div>
          <span className="text-lg font-bold text-[#4ADE80]">${load.price?.toLocaleString()}</span>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Status Card */}
        <Card className={`border-2 ${isDelivered ? 'border-green-500/30 bg-green-500/5' : 'border-[#3B82F6]/20 bg-[#0D1B2A]'}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {isDelivered ? (
                  <CheckCircle className="w-8 h-8 text-green-400" />
                ) : (
                  <Package className="w-8 h-8 text-[#4ADE80]" />
                )}
                <div>
                  <p className="text-xs text-gray-400 uppercase">Status</p>
                  <p className={`font-bold capitalize ${isDelivered ? 'text-green-400' : 'text-white'}`}>
                    {load.assignment_status?.replace('_', ' ')}
                  </p>
                </div>
              </div>
              {load.distance && (
                <div className="text-right">
                  <p className="text-xs text-gray-400">Distance</p>
                  <p className="font-bold text-white">{load.distance} mi</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Pickup Location */}
        <Card className="border-[#3B82F6]/20 bg-[#0D1B2A]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-400 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-green-400" />
              Pickup
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="text-white font-medium">{load.pickup_location}</p>
            <div className="flex items-center gap-2 mt-1">
              <Clock className="w-3 h-3 text-gray-500" />
              <p className="text-sm text-gray-400">{formatDate(load.pickup_date)}</p>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-3 w-full border-green-500/30 text-green-400"
              onClick={() => openNavigation(load.pickup_location)}
            >
              <Navigation className="w-4 h-4 mr-2" />
              Navigate to Pickup
            </Button>
          </CardContent>
        </Card>

        {/* Delivery Location */}
        <Card className="border-[#3B82F6]/20 bg-[#0D1B2A]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-400 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-red-400" />
              Delivery
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="text-white font-medium">{load.delivery_location}</p>
            {load.delivery_date && (
              <div className="flex items-center gap-2 mt-1">
                <Clock className="w-3 h-3 text-gray-500" />
                <p className="text-sm text-gray-400">{formatDate(load.delivery_date)}</p>
              </div>
            )}
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-3 w-full border-red-500/30 text-red-400"
              onClick={() => openNavigation(load.delivery_location)}
            >
              <Navigation className="w-4 h-4 mr-2" />
              Navigate to Delivery
            </Button>
          </CardContent>
        </Card>

        {/* Shipper Contact */}
        {(load.shipper_name || load.shipper_phone) && (
          <Card className="border-[#3B82F6]/20 bg-[#0D1B2A]">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-400">Shipper Contact</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {load.shipper_name && <p className="text-white font-medium">{load.shipper_name}</p>}
              {load.shipper_contact && <p className="text-sm text-gray-400">{load.shipper_contact}</p>}
              <div className="flex gap-2 mt-3">
                {load.shipper_phone && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1 border-[#3B82F6]/30"
                    onClick={() => callNumber(load.shipper_phone)}
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Call
                  </Button>
                )}
                {load.shipper_email && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1 border-[#3B82F6]/30"
                    onClick={() => window.location.href = `mailto:${load.shipper_email}`}
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Email
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Special Instructions */}
        {load.special_instructions && (
          <Card className="border-yellow-500/20 bg-yellow-500/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-yellow-400 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Special Instructions
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <p className="text-white text-sm">{load.special_instructions}</p>
            </CardContent>
          </Card>
        )}

        {/* POD Info (if delivered) */}
        {load.proof_of_delivery && (
          <Card className="border-green-500/20 bg-green-500/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-green-400 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Proof of Delivery
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <p className="text-white"><strong>Received by:</strong> {load.proof_of_delivery.recipient_name}</p>
              {load.proof_of_delivery.delivery_notes && (
                <p className="text-sm text-gray-400 mt-1">{load.proof_of_delivery.delivery_notes}</p>
              )}
              <p className="text-xs text-gray-500 mt-2">
                Delivered: {formatDate(load.proof_of_delivery.delivered_at)}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <Card className="border-[#3B82F6]/20 bg-[#0D1B2A]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-400">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-2 gap-2">
              <Button 
                variant="outline" 
                className="h-auto py-3 flex flex-col items-center gap-1 border-[#3B82F6]/30"
                onClick={() => navigate(`/driver/bol/${loadId}`)}
              >
                <FileText className="w-5 h-5 text-[#3B82F6]" />
                <span className="text-xs">View BOL</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-auto py-3 flex flex-col items-center gap-1 border-[#3B82F6]/30"
                onClick={() => navigate('/driver/documents')}
              >
                <Camera className="w-5 h-5 text-[#4ADE80]" />
                <span className="text-xs">Scan Docs</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Action Bar */}
      {!isDelivered && (
        <div className="fixed bottom-0 left-0 right-0 bg-[#0D1B2A] border-t border-[#3B82F6]/20 p-4 safe-area-inset-bottom">
          <Button 
            className="w-full bg-[#4ADE80] hover:bg-[#22C55E] text-black h-12 text-lg"
            onClick={() => {
              if (load.assignment_status === 'in_transit') {
                setShowPODDialog(true);
              } else {
                setShowStatusDialog(true);
              }
            }}
          >
            {load.assignment_status === 'in_transit' ? (
              <>
                <CheckCircle className="w-5 h-5 mr-2" />
                Complete Delivery
              </>
            ) : (
              <>
                <Play className="w-5 h-5 mr-2" />
                {getStatusButtonText()}
              </>
            )}
          </Button>
        </div>
      )}

      {/* Status Update Dialog */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent className="bg-[#0D1B2A] border-[#3B82F6]/20 max-w-sm mx-4">
          <DialogHeader>
            <DialogTitle>{getStatusButtonText()}</DialogTitle>
            <DialogDescription>
              Update the load status to {getNextStatus()?.replace('_', ' ')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={statusNotes}
                onChange={(e) => setStatusNotes(e.target.value)}
                placeholder="Any notes about this status update..."
                className="mt-1 bg-[#0A1628] border-[#3B82F6]/20"
              />
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowStatusDialog(false)} className="flex-1">
                Cancel
              </Button>
              <Button 
                onClick={() => handleUpdateStatus(getNextStatus())}
                disabled={updating}
                className="flex-1 bg-[#4ADE80] hover:bg-[#22C55E] text-black"
              >
                {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* POD Dialog */}
      <Dialog open={showPODDialog} onOpenChange={setShowPODDialog}>
        <DialogContent className="bg-[#0D1B2A] border-[#3B82F6]/20 max-w-sm mx-4 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Proof of Delivery</DialogTitle>
            <DialogDescription>
              Capture recipient signature to complete delivery
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Recipient Name *</Label>
              <Input
                value={podForm.recipient_name}
                onChange={(e) => setPodForm({...podForm, recipient_name: e.target.value})}
                placeholder="Name of person receiving"
                className="mt-1 bg-[#0A1628] border-[#3B82F6]/20"
              />
            </div>
            
            <div>
              <Label>Signature *</Label>
              <div className="mt-1 border border-[#3B82F6]/20 rounded-lg overflow-hidden bg-[#0A1628]">
                <canvas
                  ref={signatureCanvasRef}
                  width={280}
                  height={150}
                  className="w-full touch-none"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={clearSignature}
                className="mt-1 text-xs text-gray-400"
              >
                Clear Signature
              </Button>
            </div>
            
            <div>
              <Label>Delivery Notes (optional)</Label>
              <Textarea
                value={podForm.delivery_notes}
                onChange={(e) => setPodForm({...podForm, delivery_notes: e.target.value})}
                placeholder="Any delivery notes..."
                className="mt-1 bg-[#0A1628] border-[#3B82F6]/20"
                rows={2}
              />
            </div>
            
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowPODDialog(false)} className="flex-1">
                Cancel
              </Button>
              <Button 
                onClick={handleSubmitPOD}
                disabled={updating}
                className="flex-1 bg-[#4ADE80] hover:bg-[#22C55E] text-black"
              >
                {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Complete Delivery'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* SOS Emergency Button */}
      <SOSButton loadId={loadId} />
    </div>
  );
};

export default DriverLoadDetail;
